import { useCallback, useEffect, useRef } from 'react';
import { useOrganization, useOrganizationList } from '@clerk/clerk-react';
import { toast } from '@strivelabs/ui-core/v2';
import { showCreateOrg } from '@/constants';
import { logger, logInfo } from '@/lib/logger';
import { ANALYTICS_EVENTS, useTrack } from '@/lib/analytics';

/**
 * Organisation state and switching, lifted out of the presentation so the
 * header component stays declarative.
 *
 * Behaviour is carried over from the floating rail's switcher: if the user has
 * no active organisation, the first membership is selected automatically, and
 * switching reloads the page because tenant-scoped data is cached per org.
 */
export function useOrgSwitcher() {
  const { isLoaded: isOrgLoaded, organization, membership } = useOrganization();
  const {
    isLoaded: isListLoaded,
    setActive,
    userMemberships,
  } = useOrganizationList({
    userMemberships: { infinite: true, pageSize: 50 },
  });

  const trackEvent = useTrack();
  const memberships = userMemberships?.data;
  const isReady = isOrgLoaded && isListLoaded && memberships !== undefined;
  const canCreateOrg = showCreateOrg && membership?.role === 'org:admin';

  // Guards against a second setActive while the first is in flight, which would
  // otherwise fire again on the re-render the first one triggers.
  const isSettingActive = useRef(false);

  useEffect(() => {
    if (!isReady || organization) return;
    const firstId = memberships?.[0]?.organization?.id;
    if (!firstId || isSettingActive.current) return;

    isSettingActive.current = true;
    setActive({ organization: firstId })
      .catch((error: unknown) => {
        // Without this the rejection is unhandled and the user is left on the
        // "No organisation" row with a nav that will 403, and nothing anywhere
        // recording why.
        logger(
          error instanceof Error
            ? error
            : new Error('OrgSwitcher: Failed to select the first organization', { cause: error }),
          { context: 'OrgSwitcher: Failed to select the first organization', orgId: firstId },
        );
      })
      .finally(() => {
        isSettingActive.current = false;
      });
  }, [isReady, organization, memberships, setActive]);

  const switchOrg = useCallback(
    async (orgId: string) => {
      if (!orgId || !setActive) return;
      const target = memberships?.find(entry => entry.organization.id === orgId);
      if (!target) return;

      try {
        logInfo('OrgSwitcher: Attempting to switch organization', {
          targetOrgId: orgId,
          currentOrgId: organization?.id,
        });
        trackEvent(ANALYTICS_EVENTS.ORGANIZATION_SWITCHED, {
          to_organization_id: target.organization.id,
        });
        await setActive({ organization: target.organization.id });
        logInfo('OrgSwitcher: Successfully switched organization', {
          newOrgId: target.organization.id,
          newOrgName: target.organization.name,
        });
        // Tenant-scoped caches are keyed per organisation, so a reload is the
        // cheapest way to guarantee nothing from the previous org survives.
        window.location.reload();
      } catch (error) {
        logger(
          error instanceof Error
            ? error
            : new Error('OrgSwitcher: Failed to switch organization', { cause: error }),
          {
            context: 'OrgSwitcher: Failed to switch organization',
            targetOrgId: orgId,
            currentOrgId: organization?.id,
          },
        );
        // The menu closes on click, so without this the user picks an
        // organisation, the panel shuts, the reload never comes, and they are
        // left looking at the old organisation with no idea anything failed.
        toast.error('Could not switch organisation');
      }
    },
    [setActive, memberships, organization?.id, trackEvent],
  );

  return {
    isReady,
    organization,
    memberships: memberships ?? [],
    hasMultipleOrgs: (memberships?.length ?? 0) > 1,
    canCreateOrg,
    switchOrg,
  };
}
